import useStore from "stores/index";
import { ESS_OPEN, ESS_CLOSED, ESS_CONNECTING, ESS_CONNECT_FATAL } from "stores/eventServiceStatusStore";
import { retryAsync } from "../utils/retryAsync";
import * as eventApi from "../api/eventApi";
import * as api from "../api/api";

import { useUserSessionStore } from "stores/v2/userSessionStore";

class EventService {
	retryBackoffSecs = 1;
	retriedWithAuth = false;
	eventServiceUrl = null;
	apiDispUrl = null;
	eventSource = null;

	connect = async () => {
		if (this.eventSource) return;

		const username = localStorage.getItem("USERNAME");
		const deviceId = localStorage.getItem("DEVICE");

		try {
			this._updateStatusContext(ESS_CONNECTING);
			this.eventServiceUrl = await retryAsync(5, 1000, eventApi.getEventServiceUrl);
		} catch (error) {
			this._updateStatusContext(ESS_CONNECT_FATAL);
			console.error("Failed to fetch event service or api-disp URL");
			throw error;
		}
		this.eventSource = new EventSource(
			`${this.eventServiceUrl}/events/v2/appliance/${deviceId}/recipient/${username}?events=MESSAGE,SCREENSHOT_UPDATE`,
			{ withCredentials: true }
		);
		this._updateStatusContext(ESS_CONNECTING);

		this.eventSource.onerror = (error) => {
			this._updateStatusContext(ESS_CONNECTING);
			this.disconnect(true);

			if (!this.retriedWithAuth) {
				this._retryConnectWithAuth();
				return;
			}

			setTimeout(() => {
				this.connect();
			}, this.retryBackoffSecs * 1000);
			this.retryBackoffSecs = Math.min(64, this.retryBackoffSecs * 2);
		};

		this.eventSource.onopen = (error) => {
			this.retriedWithAuth = false;
			this._updateStatusContext(ESS_OPEN);
		};
	};

	disconnect = (retrying) => {
		if (this.eventSource) {
			this.eventSource.close();
			if (!retrying) {
				this.retriedWithAuth = false;
				this._updateStatusContext(ESS_CLOSED);
			}
		}
		this.eventSource = null;
	};

	_updateStatusContext = (status) => {
		useStore.getState().eventServiceStatus.setStatus(status);
	};

	_retryConnectWithAuth = async () => {
		const onAuthFailureHandler = (response) => {
			if (response.status === 401 || response.status === 403) {
				this.disconnect();
				useUserSessionStore.getState().handleInvalidTokenError(new Error("Session is no longer valid"));
			}

			return;
		};

		try {
			const fetchedApiDispUrl = await api.get("/api-url", {}, true, onAuthFailureHandler);

			this.apiDispUrl = fetchedApiDispUrl.result;

			await api.fetch(
				`${this.apiDispUrl}/authenticate/set-cookie?access_token=${localStorage.getItem("TOKEN")}`,
				{
					method: "POST",
					mode: "no-cors",
					credentials: "include",
				},
				false,
				onAuthFailureHandler
			);
		} catch (error) {
			console.error("Failed to authenticate before retrying to connect to event-service, triggering connect retry again after timeout");
			this.retryBackoffSecs = Math.min(64, this.retryBackoffSecs * 2);
			setTimeout(this.connect, this.retryBackoffSecs * 1000);
			return;
		}

		this.retriedWithAuth = true;
		this.connect();
	};
}

const eventService = new EventService();
export default eventService;
